// src/modules/Dashboard.tsx
import React from 'react';
import { 
  UserIcon, ClipboardList, Users, BookOpen, 
  Shield, FileText, Package, TrendingUp, TrendingDown, Receipt
} from 'lucide-react';
import { User } from '../types';

export function Dashboard({ user }: { user: User }) {
  // ── Load all data from localStorage ──
  const residents = JSON.parse(localStorage.getItem('rmacs_residents') || '[]');
  const audits = JSON.parse(localStorage.getItem('rmacs_audits') || '[]');
  const staff = JSON.parse(localStorage.getItem('rmacs_staff') || '[]');
  const training = JSON.parse(localStorage.getItem('rmacs_training_completions') || '[]');
  const dols = JSON.parse(localStorage.getItem('rmacs_dols') || '[]');
  const policies = JSON.parse(localStorage.getItem('rmacs_policies') || '[]');
  const policyReads = JSON.parse(localStorage.getItem('rmacs_policy_reads') || '[]');
  const inventory = JSON.parse(localStorage.getItem('rmacs_inventory_stock') || '[]');
  const invoices = JSON.parse(localStorage.getItem('rmacs_invoices') || '[]');

  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

  // ── Calculate stats ──
  const totalResidents = residents.length;
  const totalAudits = audits.length;
  const totalStaff = staff.length;
  const totalTraining = training.length;
  const activeDols = dols.filter((d: any) => d.current_status !== 'Expired' && d.current_status !== 'Refused' && d.current_status !== 'Withdrawn').length;
  const totalPolicies = policies.length;
  const uniqueReaders = new Set(policyReads.map((r: any) => r.userId)).size;
  const lowStockItems = inventory.filter((i: any) => i.current_stock < i.min_required).length;

  const admittedThisMonth = residents.filter((r: any) => r.admission_date && r.admission_date.startsWith(currentMonth)).length;
  const exitedThisMonth = residents.filter((r: any) => r.exit_date && r.exit_date.startsWith(currentMonth)).length;

  const invoicesThisMonth = invoices.filter((inv: any) => inv.invoice_date && inv.invoice_date.startsWith(currentMonth)).length;
  const totalInvoiceAmount = invoices
    .filter((inv: any) => inv.invoice_date && inv.invoice_date.startsWith(currentMonth))
    .reduce((sum: number, inv: any) => sum + (inv.total_amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
        <h2 className="text-2xl font-bold text-[#123B36]">Dashboard</h2>
        <p className="text-[#6B7A76]">Welcome back, {user.name}. Here's your compliance overview.</p>
      </div>

      {/* Row 1: Core stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <UserIcon className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{totalResidents}</p>
          <p className="text-sm text-[#6B7A76]">Total Residents</p>
          <div className="flex justify-center gap-4 text-xs text-[#6B7A76] mt-1">
            <span>Admitted: {admittedThisMonth}</span>
            <span>Exited: {exitedThisMonth}</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <ClipboardList className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{totalAudits}</p>
          <p className="text-sm text-[#6B7A76]">Audits Completed</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <Users className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{totalStaff}</p>
          <p className="text-sm text-[#6B7A76]">Staff Members</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <BookOpen className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{totalTraining}</p>
          <p className="text-sm text-[#6B7A76]">Training Records</p>
        </div>
      </div>

      {/* Row 2: Extra stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <Shield className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{activeDols}</p>
          <p className="text-sm text-[#6B7A76]">Active DoLS</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <FileText className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{totalPolicies}</p>
          <p className="text-sm text-[#6B7A76]">Policies</p>
          <p className="text-xs text-[#6B7A76]">{uniqueReaders} staff have read</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <Package className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{lowStockItems}</p>
          <p className="text-sm text-[#6B7A76]">Low Stock Alerts</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 text-center">
          <Receipt className="w-8 h-8 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{invoicesThisMonth}</p>
          <p className="text-sm text-[#6B7A76]">Invoices (This Month)</p>
          <p className="text-xs text-[#6B7A76]">Total: £{totalInvoiceAmount.toFixed(2)}</p>
        </div>
      </div>
    </div>
  );
}